import React, { useState, useEffect } from 'react';
import { FaTools, FaCamera, FaShieldAlt, FaSyringe, FaLaptop, FaBuilding, FaBox, FaCalendarAlt, FaUserInjured, FaExclamationTriangle, FaPhone, FaCheckCircle } from 'react-icons/fa';

const EquipmentForm = ({ category, onSubmit }) => {
  const [form, setForm] = useState({
    equipmentCategory: category,
    equipmentName: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    location: '',
    dateOfEvent: new Date().toISOString().split('T')[0],
    timeOfEvent: '',
    procedureImpacted: '',
    patientImpact: 'No',
    downtimeMinutes: '',
    issueType: '',
    description: '',
    actionTaken: '',
    vendorContacted: 'No',
    vendorTicket: '',
    requiresFollowUp: 'No'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit(form);
      setIsSubmitting(false);
    }, 100);
  };

  // Get icon based on category
  const getCategoryIcon = () => {
    if (category.includes('Surgical')) return FaTools;
    if (category.includes('Imaging')) return FaCamera;
    if (category.includes('Sterilization')) return FaShieldAlt;
    if (category.includes('Anesthesia')) return FaSyringe;
    if (category.includes('Computer') || category.includes('Software')) return FaLaptop;
    if (category.includes('Facility')) return FaBuilding;
    return FaTools; // Default icon
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-indigo-100">
            <CategoryIcon className="size-8 text-indigo-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {category}
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report equipment malfunction and document the incident
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={submit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Equipment Information Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBox className="size-5 text-gray-400" />
                  Equipment Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Basic details about the malfunctioning equipment
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="equipmentName" className="block text-sm/6 font-medium text-gray-900">
                      Equipment Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="equipmentName"
                        name="equipmentName"
                        type="text"
                        value={form.equipmentName}
                        onChange={update}
                        required
                        placeholder="Enter equipment name"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="manufacturer" className="block text-sm/6 font-medium text-gray-900">
                      Manufacturer
                    </label>
                    <div className="mt-2">
                      <input
                        id="manufacturer"
                        name="manufacturer"
                        type="text"
                        value={form.manufacturer}
                        onChange={update}
                        placeholder="Enter manufacturer"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="model" className="block text-sm/6 font-medium text-gray-900">
                      Model
                    </label>
                    <div className="mt-2">
                      <input
                        id="model"
                        name="model"
                        type="text"
                        value={form.model}
                        onChange={update}
                        placeholder="Enter model number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="serialNumber" className="block text-sm/6 font-medium text-gray-900">
                      Serial Number
                    </label>
                    <div className="mt-2">
                      <input
                        id="serialNumber"
                        name="serialNumber"
                        type="text"
                        value={form.serialNumber}
                        onChange={update}
                        placeholder="Enter serial number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm/6 font-medium text-gray-900">
                      Location/Operatory
                    </label>
                    <div className="mt-2">
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={form.location}
                        onChange={update}
                        placeholder="Enter location"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and where the malfunction occurred
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateOfEvent" className="block text-sm/6 font-medium text-gray-900">
                      Date of Event <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateOfEvent"
                        name="dateOfEvent"
                        type="date"
                        value={form.dateOfEvent}
                        onChange={update}
                        required
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeOfEvent" className="block text-sm/6 font-medium text-gray-900">
                      Time of Event
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeOfEvent"
                        name="timeOfEvent"
                        type="time"
                        value={form.timeOfEvent}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="procedureImpacted" className="block text-sm/6 font-medium text-gray-900">
                      Procedure Impacted
                    </label>
                    <div className="mt-2">
                      <input
                        id="procedureImpacted"
                        name="procedureImpacted"
                        type="text"
                        value={form.procedureImpacted}
                        onChange={update}
                        placeholder="Enter procedure type"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="downtimeMinutes" className="block text-sm/6 font-medium text-gray-900">
                      Downtime (minutes)
                    </label>
                    <div className="mt-2">
                      <input
                        id="downtimeMinutes"
                        name="downtimeMinutes"
                        type="number"
                        value={form.downtimeMinutes}
                        onChange={update}
                        placeholder="Enter downtime in minutes"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact Assessment Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUserInjured className="size-5 text-gray-400" />
                  Impact Assessment
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Assess the impact of the malfunction
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="patientImpact" className="block text-sm/6 font-medium text-gray-900">
                      Patient Impact
                    </label>
                    <div className="mt-2">
                      <select
                        id="patientImpact"
                        name="patientImpact"
                        value={form.patientImpact}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Issue Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Issue Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Describe the malfunction and its type
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="issueType" className="block text-sm/6 font-medium text-gray-900">
                      Issue Type
                    </label>
                    <div className="mt-2">
                      <select
                        id="issueType"
                        name="issueType"
                        value={form.issueType}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option value="">Select issue type</option>
                        <option>Hardware Failure</option>
                        <option>Calibration/Accuracy</option>
                        <option>Power/Network</option>
                        <option>Consumable/Sterility</option>
                        <option>User Error</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm/6 font-medium text-gray-900">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="description"
                        name="description"
                        rows={5}
                        value={form.description}
                        onChange={update}
                        required
                        placeholder="Provide a detailed description of the malfunction..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="actionTaken" className="block text-sm/6 font-medium text-gray-900">
                      Action Taken
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="actionTaken"
                        name="actionTaken"
                        rows={4}
                        value={form.actionTaken}
                        onChange={update}
                        placeholder="Describe any actions taken to address the issue..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Vendor Information Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPhone className="size-5 text-gray-400" />
                  Vendor Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Vendor contact and follow-up details
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="vendorContacted" className="block text-sm/6 font-medium text-gray-900">
                      Vendor Contacted
                    </label>
                    <div className="mt-2">
                      <select
                        id="vendorContacted"
                        name="vendorContacted"
                        value={form.vendorContacted}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="vendorTicket" className="block text-sm/6 font-medium text-gray-900">
                      Vendor Ticket/Ref #
                    </label>
                    <div className="mt-2">
                      <input
                        id="vendorTicket"
                        name="vendorTicket"
                        type="text"
                        value={form.vendorTicket}
                        onChange={update}
                        placeholder="Enter ticket or reference number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="requiresFollowUp" className="block text-sm/6 font-medium text-gray-900">
                      Requires Follow-up
                    </label>
                    <div className="mt-2">
                      <select
                        id="requiresFollowUp"
                        name="requiresFollowUp"
                        value={form.requiresFollowUp}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EquipmentForm;
